/**
 * ErrorBoundary
 *
 * Catches uncaught render errors so a bug in one screen doesn't white-screen
 * the whole app — especially important on the kids' side where the child has
 * no devtools and no way to recover. Logs the error and offers a "Go home" path.
 */

import { Component, type ReactNode } from 'react';

/**
 * A tab left open across a deploy is running the previous build, whose lazily
 * imported chunks no longer exist on the server. The static host answers the
 * missing file with index.html, so the browser reports a MIME-type error rather
 * than a 404 — and every route-level `lazy()` in the app fails.
 *
 * The page is simply stale, and a reload fixes it, so do that instead of showing
 * an error screen for something the user cannot act on.
 */
export function isStaleChunkError(error: Error): boolean {
  const text = `${error?.name ?? ''} ${error?.message ?? ''}`;
  return (
    /Failed to fetch dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||       // Safari
    /error loading dynamically imported module/i.test(text) ||
    (/expected a javascript/i.test(text) && /mime type/i.test(text))
  );
}

/**
 * Reload once, and only once. sessionStorage survives the reload, so if the new
 * build is genuinely broken we fall through to the error screen instead of
 * refreshing forever.
 */
// Deliberately never cleared: at most one automatic reload per browser session.
// If the freshly loaded build fails the same way, the user sees the error screen
// rather than an endless refresh loop.
const RELOAD_GUARD = 'yoluno:stale-chunk-reloaded';

function reloadOnceForStaleBuild(): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD)) return false;
    sessionStorage.setItem(RELOAD_GUARD, String(Date.now()));
  } catch {
    return false; // storage blocked — better to show the error than loop
  }
  window.location.reload();
  return true;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // A stale build isn't a bug the user can do anything about — reload into the
    // current one rather than showing them a failure screen.
    if (isStaleChunkError(error) && reloadOnceForStaleBuild()) return;

    // Don't swallow — surface to the console so devs can diagnose. A real
    // app would also send this to an error tracker (Sentry / similar).
    console.error('[ErrorBoundary] Render error:', error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return <DefaultErrorFallback onReset={this.handleReset} />;
  }
}

function DefaultErrorFallback({ onReset }: { onReset: () => void }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{
        background: 'linear-gradient(165deg, #FDF6E8 0%, #FEF0EA 50%, #F3EFF8 100%)',
        fontFamily: "var(--font-body)",
      }}
    >
      <div className="text-center max-w-md">
        <div style={{ fontSize: 56 }} className="mb-4">🌥️</div>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--navy-800)', marginBottom: 8 }}>
          Oops! Something went sideways.
        </h1>
        <p style={{ fontSize: 15, color: 'var(--navy-600)', marginBottom: 24, lineHeight: 1.5 }}>
          We've made a note of it. Try going back to the start — that usually fixes it.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={onReset}
            className="px-5 py-2.5 rounded-full text-[14px]"
            style={{ background: 'var(--surface-card)', border: '1.5px solid var(--gold-500)', color: 'var(--gold-500)', fontWeight: 600 }}
          >
            Try again
          </button>
          <button
            onClick={() => (window.location.href = '/')}
            className="px-5 py-2.5 rounded-full text-[14px]"
            style={{ background: 'var(--gold-500)', color: 'var(--text-on-brand)', fontWeight: 600 }}
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );
}
